import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  House, Package, Coins, ShoppingBagOpen, ArrowsClockwise, Receipt,
  QrCode, ShoppingCart, PenNib, Scales, Sparkle, AddressBook, Truck,
  ClockCounterClockwise, PlugsConnected, Broadcast, UsersThree, Sun, Moon,
  MagnifyingGlass, SignOut, List, X, CaretDown, CaretDoubleLeft, CaretDoubleRight,
  Bell, User, ChartBar, CurrencyInr, ChatCircleText,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: House, testid: "nav-dashboard" },
      { to: "/ai", label: "AI assistant", icon: ChatCircleText, testid: "nav-ai" },
      { to: "/analytics", label: "AI insights", icon: Sparkle, testid: "nav-analytics" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/production", label: "Production", icon: Package, testid: "nav-production" },
      { to: "/inventory", label: "Inventory", icon: Coins, testid: "nav-inventory" },
      { to: "/sales", label: "Sales orders", icon: ShoppingBagOpen, testid: "nav-sales" },
      { to: "/exchanges", label: "Exchanges", icon: ArrowsClockwise, testid: "nav-exchanges" },
      { to: "/purchase", label: "Purchase", icon: ShoppingCart, testid: "nav-purchase" },
      { to: "/dispatch", label: "QR dispatch", icon: QrCode, testid: "nav-dispatch" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/finance", label: "Finance & accounts", icon: CurrencyInr, testid: "nav-finance" },
      { to: "/documents", label: "Invoices & challans", icon: Receipt, testid: "nav-documents" },
    ],
  },
  {
    label: "Catalogue & legal",
    items: [
      { to: "/designs", label: "Designs & catalogue", icon: PenNib, testid: "nav-designs" },
      { to: "/legal", label: "Legal", icon: Scales, testid: "nav-legal" },
    ],
  },
  {
    label: "Directory",
    items: [
      { to: "/contacts", label: "Contacts", icon: AddressBook, testid: "nav-contacts" },
      { to: "/shipping", label: "Shipping", icon: Truck, testid: "nav-shipping" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/audit", label: "Audit log", icon: ClockCounterClockwise, testid: "nav-audit" },
      { to: "/integration", label: "Integration health", icon: PlugsConnected, testid: "nav-integration" },
      { to: "/simulator", label: "Website simulator", icon: Broadcast, testid: "nav-simulator" },
      { to: "/users", label: "Users & roles", icon: UsersThree, testid: "nav-users" },
    ],
  },
];

const ROUTE_FOR_KIND = {
  order: "/sales",
  product: "/designs",
  contact: "/contacts",
  material: "/inventory",
  production: "/production",
  purchase: "/purchase",
  document: "/documents",
  legal: "/legal",
  package: "/dispatch",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("ae-side-collapsed") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("ae-side-collapsed", collapsed ? "1" : "0"); } catch (_) { /* noop */ }
  }, [collapsed]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  // Global search
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        if (!cancel) { setResults(data.results || []); setShowResults(true); }
      } catch (_) { /* silent */ }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  // Lightweight notification derivation
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const items = [];
        const [{ data: health }, exchanges] = await Promise.all([
          api.get("/integration/health").catch(() => ({ data: null })),
          api.get("/exchanges?status=exchange_requested").catch(() => ({ data: [] })),
        ]);
        if (health?.failed_webhooks) items.push({ id: "fw", tone: "rose", title: `${health.failed_webhooks} failed webhook${health.failed_webhooks > 1 ? "s" : ""}`, body: "Website ↔ ERP integration needs attention", to: "/integration" });
        if (health?.pending_retries) items.push({ id: "pr", tone: "gold", title: `${health.pending_retries} pending retries`, body: "Events awaiting redelivery", to: "/integration" });
        if (exchanges?.data?.length) items.push({ id: "ex", tone: "gold", title: `${exchanges.data.length} exchange requests`, body: "Awaiting your approval", to: "/exchanges" });
        if (!items.length) items.push({ id: "ok", tone: "sage", title: "All caught up", body: "No pending alerts right now.", to: null });
        if (!cancel) setNotifs(items);
      } catch (_) { /* silent */ }
    };
    load();
    const int = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(int); };
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.querySelector('[data-testid="global-search"]')?.focus();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const routeForResult = (r) => ROUTE_FOR_KIND[r._kind] || "/";
  const sideWidth = collapsed ? "w-[68px]" : "w-[236px]";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-40 h-screen shrink-0 border-r border-border bg-[color:hsl(var(--sidebar))] transition-[transform,width] duration-300 ease-out ${sideWidth} ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        {/* Brand */}
        <div className="h-16 border-b border-border/70 px-3 flex items-center justify-between">
          <div className={`flex items-center gap-2.5 min-w-0 ${collapsed ? "justify-center w-full" : "pl-2"}`}>
            <div className="w-9 h-9 rounded-lg bg-foreground text-background flex items-center justify-center font-display font-bold leading-none shrink-0">
              Æ
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-display text-[15px] font-semibold leading-tight tracking-tight">Asymmetry</div>
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground font-medium mt-0.5">Edits · ERP</div>
              </div>
            )}
          </div>
          <button
            className="lg:hidden text-muted-foreground hover:text-foreground"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-close"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="py-4 overflow-y-auto h-[calc(100vh-64px-52px)] px-3">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? "mt-6" : ""}>
              {!collapsed && (
                <div className="label-eyebrow px-2 mb-2">{group.label}</div>
              )}
              {collapsed && gi > 0 && <div className="divider-line mx-2 mb-3" />}
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    onClick={() => setMobileOpen(false)}
                    data-testid={testid}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) => `side-link ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}
                  >
                    <Icon size={17} weight="regular" className="shrink-0" />
                    {!collapsed && <span className="truncate">{label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 h-[52px] border-t border-border/70 bg-[color:hsl(var(--sidebar))]/95 backdrop-blur">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={`hidden lg:flex items-center gap-2 w-full h-full px-4 text-xs text-muted-foreground hover:text-foreground transition-colors ${collapsed ? "justify-center" : ""}`}
            data-testid="sidebar-collapse"
          >
            {collapsed ? <CaretDoubleRight size={14} /> : <CaretDoubleLeft size={14} />}
            {!collapsed && <span className="uppercase tracking-[0.14em] font-medium">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Content column */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="h-full px-4 md:px-8 flex items-center gap-3">
            <button
              className="lg:hidden text-foreground/80 hover:text-foreground p-1"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-open"
            >
              <List size={22} />
            </button>

            {/* Global search */}
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass
                size={15}
                weight="regular"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                data-testid="global-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => q.length >= 2 && setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 150)}
                placeholder="Search orders, products, contacts…"
                className="input-soft pl-9 pr-14 h-10"
              />
              <kbd className="hidden md:inline-flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-border rounded bg-muted/70 text-muted-foreground">
                ⌘K
              </kbd>
              {showResults && results.length > 0 && (
                <div className="absolute z-40 top-full mt-2 left-0 right-0 modal-surface overflow-hidden modal-scale">
                  {results.slice(0, 20).map((r) => (
                    <button
                      key={`${r._kind}-${r._id}`}
                      className="w-full text-left px-3.5 py-2.5 border-b border-border/60 last:border-b-0 hover:bg-muted/60 flex items-center justify-between transition-colors"
                      onMouseDown={() => { nav(routeForResult(r)); setQ(""); }}
                      data-testid={`search-result-${r._kind}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm truncate">
                          {r.name || r.order_number || r.doc_number || r.po_number || r.package_id || r.sku || r.code}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 capitalize">{r._kind}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0 ml-3">
                        {r.status || r.contact_type || r.category || ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1" />

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => { setShowNotif((v) => !v); setShowProfile(false); }}
                className="btn-ghost btn-icon relative"
                data-testid="notif-btn"
                title="Notifications"
              >
                <Bell size={16} weight="regular" />
                {notifs.some((n) => n.tone !== "sage") && (
                  <span
                    className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-[color:hsl(var(--brand))]"
                    style={{ animation: "soft-pulse 1.8s ease-in-out infinite" }}
                  />
                )}
              </button>
              {showNotif && (
                <div className="absolute right-0 top-full mt-2 w-[340px] modal-surface overflow-hidden modal-scale" data-testid="notif-panel">
                  <div className="px-4 py-3 border-b border-border/70 flex items-center justify-between">
                    <div className="text-sm font-semibold">Notifications</div>
                    <span className="text-[11px] text-muted-foreground">{notifs.length}</span>
                  </div>
                  <div className="max-h-[380px] overflow-y-auto">
                    {notifs.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => { if (n.to) nav(n.to); setShowNotif(false); }}
                        className="w-full text-left px-4 py-3 border-b border-border/60 last:border-b-0 hover:bg-muted/60 flex items-start gap-3 transition-colors"
                      >
                        <span className={`mt-1.5 w-2 h-2 rounded-full ${
                          n.tone === "sage" ? "bg-[color:hsl(var(--success))]" :
                          n.tone === "rose" ? "bg-[color:hsl(var(--destructive))]" :
                          n.tone === "gold" ? "bg-[color:hsl(var(--warning))]" :
                          "bg-[color:hsl(var(--primary))]"
                        }`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{n.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={toggle}
              className="btn-ghost btn-icon"
              data-testid="theme-toggle"
              title="Toggle theme"
            >
              {theme === "dark" ? <Sun size={16} weight="regular" /> : <Moon size={16} weight="regular" />}
            </button>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => { setShowProfile((v) => !v); setShowNotif(false); }}
                className="flex items-center gap-2.5 h-10 pl-1.5 pr-3 rounded-lg border border-border bg-card hover:border-foreground/30 hover:bg-muted/40 transition-all duration-200"
                data-testid="profile-btn"
              >
                <div className="w-7 h-7 rounded-md bg-foreground text-background flex items-center justify-center text-[13px] font-display font-semibold">
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="hidden md:flex flex-col leading-tight items-start">
                  <span className="text-sm font-medium truncate max-w-[140px]">{user?.name}</span>
                  <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{user?.role?.replace(/_/g, " ")}</span>
                </div>
                <CaretDown size={11} className="hidden md:block text-muted-foreground" />
              </button>
              {showProfile && (
                <div className="absolute right-0 top-full mt-2 w-[280px] modal-surface overflow-hidden modal-scale" data-testid="profile-panel">
                  <div className="px-4 py-4 border-b border-border/70">
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{user?.email}</div>
                    <div className="mt-2.5 inline-flex status-chip chip-taupe">
                      <User size={10} weight="regular" />
                      {user?.role?.replace(/_/g, " ")}
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowProfile(false); logout(); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted/60 text-left transition-colors"
                    data-testid="logout-btn"
                  >
                    <SignOut size={14} weight="regular" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8 max-w-[1600px] mx-auto page-fade" key={loc.pathname}>
          <Outlet />
        </main>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 modal-backdrop z-30 fade-in"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}
    </div>
  );
}
